import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { DollarSign, TrendingUp, Users, Rocket, Clock, Sparkles, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

function fmtMoney(n) {
  return `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatTile({ icon: Icon, label, value, sub, accent = "amber", testid, badge }) {
  const accents = {
    amber: "from-amber-500 to-orange-500",
    emerald: "from-emerald-500 to-teal-500",
    indigo: "from-indigo-500 to-violet-500",
    stone: "from-stone-500 to-stone-700",
  };
  return (
    <div className="rounded-2xl bg-white border border-stone-200 p-6 flex flex-col gap-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-3">
        <div className={`inline-grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br ${accents[accent] || accents.amber} text-white`}>
          <Icon className="w-5 h-5" />
        </div>
        {badge && (
          <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-stone-500 bg-stone-100 border border-stone-200 rounded-full px-2 py-0.5">
            {badge}
          </span>
        )}
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">{label}</div>
        <div className="mt-1 font-display text-3xl font-bold tracking-tight text-stone-900">{value}</div>
        {sub && <div className="mt-1 text-xs text-stone-500">{sub}</div>}
      </div>
    </div>
  );
}

export default function AdminRevenue() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/subscriptions/admin/revenue`);
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin access required");
      else toast.error("Failed to load revenue metrics");
      setData({});
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (data === null) {
    return (
      <div className="text-stone-500 text-sm flex items-center gap-2 py-16 justify-center">
        <Loader2 className="w-5 h-5 animate-spin" /> Crunching numbers…
      </div>
    );
  }

  const breakdown = data.tiered_breakdown || {};

  return (
    <div className="space-y-8" data-testid="admin-revenue-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-amber-600 font-semibold">Admin</div>
          <h1 className="mt-1 font-display text-3xl font-bold">Revenue</h1>
          <p className="mt-1 text-stone-600 text-sm">Real numbers pulled from active student subscriptions. Featured tutor payments will land here once payments launch.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={refreshing} className="rounded-full" data-testid="revenue-refresh">
          {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          Refresh
        </Button>
      </div>

      {/* Core tiles */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={DollarSign}
          label="Monthly Recurring Revenue"
          value={fmtMoney(data.mrr_dollars)}
          sub={`Annual run rate: ${fmtMoney(data.annual_run_rate_dollars)}`}
          accent="amber"
          testid="tile-mrr"
        />
        <StatTile
          icon={Users}
          label="Active paid students"
          value={(data.active_paid_students || 0).toLocaleString()}
          sub={`${data.active_trials || 0} still on trial`}
          accent="emerald"
          testid="tile-active-paid"
        />
        <StatTile
          icon={TrendingUp}
          label="Trials → paid this month"
          value={(data.trials_converted_this_month || 0).toLocaleString()}
          sub="Conversions since the 1st"
          accent="indigo"
          testid="tile-trial-conversions"
        />
        <StatTile
          icon={Rocket}
          label="Featured tutor payments"
          value={fmtMoney(data.featured_payments_dollars)}
          sub="Placeholder — activates when featured upgrades ship"
          accent="stone"
          badge="Coming soon"
          testid="tile-featured"
        />
      </div>

      {/* Tier breakdown card */}
      <div className="rounded-2xl bg-white border border-stone-200 p-6" data-testid="tier-breakdown">
        <div className="flex items-center gap-2 text-sm font-semibold text-stone-700">
          <Sparkles className="w-4 h-4 text-amber-500" /> Active students by tier
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div className="rounded-xl bg-stone-50 border border-stone-200 py-4">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-stone-500">Basic · $2</div>
            <div className="mt-1 font-display text-2xl font-bold" data-testid="tier-basic-count">{breakdown.basic || 0}</div>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-100 py-4">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-amber-700">Plus · $5</div>
            <div className="mt-1 font-display text-2xl font-bold text-amber-800" data-testid="tier-plus-count">{breakdown.plus || 0}</div>
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 py-4">
            <div className="text-xs uppercase tracking-[0.15em] font-semibold text-emerald-700">Premium · $9</div>
            <div className="mt-1 font-display text-2xl font-bold text-emerald-800" data-testid="tier-premium-count">{breakdown.premium || 0}</div>
          </div>
        </div>
        <div className="mt-4 text-xs text-stone-500 flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> Last refreshed {data.generated_at ? new Date(data.generated_at).toLocaleString() : "—"}
        </div>
      </div>

      {/* Notes */}
      <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50/60 p-5 text-sm text-stone-600 leading-relaxed">
        <strong className="text-stone-800">How MRR is calculated:</strong> each teacher&apos;s first active student pays their tier price; every additional active student adds a flat $2/mo. This mirrors your live checkout pricing. Numbers refresh live — no snapshots or delayed reporting.
      </div>
    </div>
  );
}
